import Phaser from "phaser";

/**
 * Living atmospheric backdrop rendered BEHIND the battlefield grid.
 * Replaces the dead flat void with a deep dusk-fantasy sky: a vertical
 * gradient, drifting aurora/nebula bands, parallax twinkling stars,
 * distant mountain silhouettes, and embers floating up across the screen.
 *
 * All objects sit at negative depth so the grid, towers, creeps and FX
 * always draw on top. Purely cosmetic — no gameplay hooks.
 */
export class Background {
  private scene: Phaser.Scene;
  private readonly objects: Phaser.GameObjects.GameObject[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    const { width, height } = scene.scale;
    this.buildSky(width, height);
    this.buildNebula(width, height);
    this.buildStars(width, height);
    this.buildMountains(width, height);
    this.buildEmbers(width, height);
  }

  private add<T extends Phaser.GameObjects.GameObject>(obj: T, depth: number): T {
    (obj as unknown as Phaser.GameObjects.Components.Depth).setDepth(depth);
    this.objects.push(obj);
    return obj;
  }

  /** Vertical gradient: deep indigo at top → warm dusk purple near the horizon. */
  private buildSky(width: number, height: number): void {
    const g = this.scene.add.graphics();
    // Top → bottom gradient (tl, tr, bl, br).
    g.fillGradientStyle(0x080a1a, 0x080a1a, 0x2a1840, 0x3a1c38, 1);
    g.fillRect(0, 0, width, height);
    // A warm glow band low on the screen, like a setting sun behind the hills.
    g.fillStyle(0x4a2030, 0.5);
    g.fillEllipse(width * 0.5, height * 0.62, width * 1.6, height * 0.5);
    this.add(g, -100);
  }

  /** Slow drifting aurora/nebula bands that cycle hue subtly. */
  private buildNebula(width: number, height: number): void {
    const palettes = [
      { color: 0x5a2a8a, y: height * 0.18, w: width * 1.4, h: 150, alpha: 0.22 },
      { color: 0x2a4a9a, y: height * 0.32, w: width * 1.7, h: 190, alpha: 0.18 },
      { color: 0x8a2a6a, y: height * 0.5, w: width * 1.5, h: 160, alpha: 0.16 },
    ];
    palettes.forEach((p, i) => {
      const neb = this.scene.add.ellipse(width * 0.5, p.y, p.w, p.h, p.color, p.alpha);
      this.add(neb, -92 + i);
      // Slow horizontal sway + breathing scale.
      this.scene.tweens.add({
        targets: neb,
        x: width * 0.5 + (i % 2 === 0 ? 40 : -40),
        scaleX: { from: 1, to: 1.15 },
        scaleY: { from: 1, to: 1.1 },
        alpha: { from: p.alpha, to: p.alpha * 1.5 },
        yoyo: true,
        repeat: -1,
        duration: 6000 + i * 1500,
        ease: "Sine.easeInOut",
      });
    });
  }

  /** Parallax starfield: two layers, far stars twinkle slowly, near stars brighter. */
  private buildStars(width: number, height: number): void {
    const rng = mulberry32(0x5eed42);
    const skyBottom = height * 0.72; // keep stars above the horizon/hills
    for (let i = 0; i < 90; i++) {
      const far = rng() < 0.6;
      const x = rng() * width;
      const y = rng() * skyBottom;
      const r = far ? 0.6 + rng() * 0.7 : 1 + rng() * 1.3;
      const tint = far ? 0x9fb4ff : 0xffffff;
      const star = this.scene.add.circle(x, y, r, tint, far ? 0.7 : 0.95);
      this.add(star, far ? -90 : -88);
      // Twinkle — randomized phase so they don't pulse in sync.
      this.scene.tweens.add({
        targets: star,
        alpha: { from: star.alpha, to: far ? 0.2 : 0.4 },
        duration: 900 + rng() * 2200,
        yoyo: true,
        repeat: -1,
        delay: rng() * 2000,
        ease: "Sine.easeInOut",
      });
    }
    // A couple of slow shooting stars.
    for (let i = 0; i < 2; i++) {
      this.spawnShootingStar(width, height, i * 4000);
    }
  }

  private spawnShootingStar(width: number, height: number, delay: number): void {
    const startX = width * (0.3 + Math.random() * 0.6);
    const startY = height * (0.05 + Math.random() * 0.2);
    const star = this.scene.add.line(0, 0, 0, 0, 18, 10, 0xffffff, 0.9).setLineWidth(1.4);
    star.setOrigin(0, 0);
    star.setPosition(startX, startY);
    star.setAlpha(0);
    this.add(star, -87);
    const fly = () => {
      star.setPosition(width * (0.3 + Math.random() * 0.6), height * (0.05 + Math.random() * 0.18));
      star.setAlpha(0);
      this.scene.tweens.add({
        targets: star,
        x: star.x - 120,
        y: star.y + 70,
        alpha: { from: 0, to: 0.9 },
        duration: 600,
        ease: "Quad.easeIn",
        onComplete: () => {
          star.setAlpha(0);
          this.scene.time.delayedCall(4000 + Math.random() * 6000, fly);
        },
      });
    };
    this.scene.time.delayedCall(delay + 1500, fly);
  }

  /** Layered distant mountain silhouettes near the horizon for depth. */
  private buildMountains(width: number, height: number): void {
    const horizon = height * 0.7;
    // Far range (darker, taller).
    const far = this.scene.add.graphics();
    far.fillStyle(0x1a1430, 0.9);
    far.beginPath();
    far.moveTo(0, horizon);
    const farPeaks = [0.0, 0.15, 0.32, 0.5, 0.68, 0.85, 1.0];
    const farH = [0, 70, 30, 90, 40, 75, 20];
    farPeaks.forEach((px, i) => far.lineTo(px * width, horizon - farH[i]));
    far.lineTo(width, horizon);
    far.lineTo(width, height);
    far.lineTo(0, height);
    far.closePath();
    far.fillPath();
    this.add(far, -84);
    // Near range (slightly lighter, lower) for parallax depth.
    const near = this.scene.add.graphics();
    near.fillStyle(0x241a3a, 0.95);
    near.beginPath();
    near.moveTo(0, horizon + 20);
    const nearPeaks = [0.0, 0.2, 0.4, 0.6, 0.8, 1.0];
    const nearH = [0, 45, 20, 55, 25, 35];
    nearPeaks.forEach((px, i) => near.lineTo(px * width, horizon + 20 - nearH[i]));
    near.lineTo(width, horizon + 20);
    near.lineTo(width, height);
    near.lineTo(0, height);
    near.closePath();
    near.fillPath();
    this.add(near, -83);
  }

  /** Embers / dust motes that drift upward across the whole screen. */
  private buildEmbers(width: number, height: number): void {
    const rng = mulberry32(0xb00b1e5);
    for (let i = 0; i < 22; i++) {
      const x = rng() * width;
      const y = rng() * height;
      const r = 0.8 + rng() * 1.6;
      const warm = rng() < 0.5;
      const ember = this.scene.add.circle(x, y, r, warm ? 0xff9a4a : 0x6ad0ff, 0.6);
      this.add(ember, -82);
      this.floatEmber(ember, width, height, rng);
    }
  }

  private floatEmber(
    ember: Phaser.GameObjects.Arc,
    width: number,
    height: number,
    rng: () => number,
  ): void {
    const driftX = (rng() - 0.5) * 60;
    const dur = 5000 + rng() * 6000;
    const startY = height + 10;
    ember.setPosition(rng() * width, startY);
    ember.setAlpha(0);
    this.scene.tweens.add({
      targets: ember,
      y: -10,
      x: ember.x + driftX,
      alpha: { from: 0, to: 0.7 },
      duration: dur,
      ease: "Sine.easeInOut",
      onComplete: () => this.floatEmber(ember, width, height, rng),
    });
    // Soft twinkle while rising.
    this.scene.tweens.add({
      targets: ember,
      scale: { from: 1, to: 1.6 },
      yoyo: true,
      repeat: -1,
      duration: 700 + rng() * 800,
      ease: "Sine.easeInOut",
    });
  }

  destroy(): void {
    for (const o of this.objects) o.destroy();
    this.objects.length = 0;
  }
}

// Deterministic RNG so the layout is stable across reloads.
function mulberry32(seed: number): () => number {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
